import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiChangelogsGetCollection } from '@r/api/generated/client.ts';
import type { ChangelogChangelogReadModel } from '@r/api/generated/client.schemas.ts';
import { LinkProtected } from '@r/components/link-protected/link-protected.tsx';
import { RichText } from '@r/components/rich-text.tsx';
import { cn } from '@r/utils/utils.ts';
import { Badge } from '@ui/badge.tsx';
import { Button } from '@ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@ui/dialog.tsx';

import { CHANGELOG_TAG_BADGE_CLASS } from '@r/components/changelogs/changelog-tag.ts';
import { consumeLoginSuccess } from '@r/components/changelogs/changelog-login-signal.ts';

const MODAL_ITEM_CAP = 5;

/**
 * Juste après une connexion réussie, `loginFn` (auth.tsx) pose un signal (mémoire, pas
 * de cache/GC à gérer). Ce composant ne se monte que dans le layout authentifié, donc
 * son tout premier rendu ne peut suivre qu'une connexion (ou un refresh de page, où le
 * signal est retombé à `false`) : au montage, on le consomme une seule fois pour appeler
 * `GET /changelogs?onlyNew=true`, et la modale ne s'ouvre que si l'API renvoie des
 * nouveautés parues depuis l'avant-dernière connexion.
 */
export function ChangelogLoginModal() {
  const { t } = useTranslation();

  // Lu et effacé une seule fois, au tout premier rendu de ce montage.
  const [triggered] = useState(() => consumeLoginSuccess());

  const query = useApiChangelogsGetCollection(
    { onlyNew: 'true', perPage: MODAL_ITEM_CAP },
    { query: { enabled: triggered, staleTime: Number.POSITIVE_INFINITY, gcTime: 0 } }
  );

  const [open, setOpen] = useState(false);

  const items = (query.data?.data ?? []) as ChangelogChangelogReadModel[];

  // N'ouvre que si l'API confirme qu'il y a effectivement des nouveautés.
  useEffect(() => {
    if (query.isSuccess && items.length > 0) setOpen(true);
  }, [query.isSuccess, items.length]);

  if (items.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="flex max-h-[min(85vh,640px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="shrink-0 border-b px-6 pb-5 pt-6">
          <DialogTitle>{t('changelog.login.modal.title')}</DialogTitle>
          <DialogDescription>{t('changelog.login.modal.subtitle')}</DialogDescription>
        </DialogHeader>

        <ul className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4">
          {items.map((changelog) => {
            const mediaUri = changelog.media?.pathUri ?? null;
            return (
              <li
                key={changelog.id}
                className={cn('overflow-hidden rounded-xl bg-body', mediaUri && 'flex')}
              >
                <div className="min-w-0 flex-1 space-y-1.5 p-3">
                  <Badge
                    className={cn(
                      CHANGELOG_TAG_BADGE_CLASS[changelog.tag],
                      'text-[10px] uppercase tracking-wide'
                    )}
                  >
                    {t(`changelog.tag.${changelog.tag}`)}
                  </Badge>
                  <p className="font-semibold leading-snug">{changelog.title}</p>
                  <RichText
                    html={changelog.description}
                    className="line-clamp-2 text-muted-foreground"
                  />
                </div>

                {mediaUri && (
                  <div className="flex w-36 shrink-0 items-center justify-center border-l p-4">
                    <img
                      src={mediaUri}
                      alt=""
                      className="max-h-full w-full rounded-lg object-contain"
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <DialogFooter className="shrink-0 border-t px-6 pb-6 pt-5">
          <LinkProtected to="/changelogs">
            <Button type="button" base="default" onClick={() => setOpen(false)}>
              {t('changelog.login.modal.cta')}
            </Button>
          </LinkProtected>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
